import { TypedDocumentNode, gql } from '@apollo/client';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import type { StuffInRewardBoxType } from '@sorare/core/src/components/rewards/RewardBoxDialog/types';
import { getCardRepresentation } from '@sorare/core/src/lib/cards';
import { isType } from '@sorare/core/src/lib/gql';
import { lazy } from '@sorare/core/src/lib/retry';

import { CardProperties } from '@sorare/marketplace/src/components/card/CardProperties';
import { TokenStatsIcon } from '@sorare/marketplace/src/components/token/TokenStatsIcon';

import type {
  CardInRewardBox_ProbabilisticBundleSlotItemInterface,
  CardInRewardBox_ProbabilisticBundleSlotItemInterface_ProbabilisticBundleSlotCardItem_,
} from './__generated__/index.graphql';

const CardPreloader = lazy(
  async () => import('@sorare/trois/src/atoms/preloaders/CardPreloader'),
  'CardPreloader'
);
const CardInRewardBoxTrois = lazy(
  async () => import('@sorare/trois/src/components/RewardBox/CardInRewardBox'),
  'CardInRewardBox'
);

type CardInRewardBoxPreloaderProps = {
  item: CardInRewardBox_ProbabilisticBundleSlotItemInterface;
};

type Props = {
  visible: boolean;
} & CardInRewardBoxPreloaderProps;
const CardInRewardBoxCanHandle = (
  item: CardInRewardBox_ProbabilisticBundleSlotItemInterface
): item is CardInRewardBox_ProbabilisticBundleSlotItemInterface_ProbabilisticBundleSlotCardItem_ => {
  return isType(item, 'ProbabilisticBundleSlotCardItem');
};

export const CardInRewardBox: StuffInRewardBoxType<
  CardInRewardBox_ProbabilisticBundleSlotItemInterface
> = ({ item, visible }: Props) => {
  if (!CardInRewardBoxCanHandle(item)) {
    return null;
  }
  const { anyCard } = item;

  return (
    <CardInRewardBoxTrois
      visible={visible}
      card={getCardRepresentation(anyCard)}
      stats={
        <Horizontal>
          <CardProperties card={anyCard} withTransferMalus={false} />
          <TokenStatsIcon card={anyCard} />
        </Horizontal>
      }
    />
  );
};

CardInRewardBox.fragments = {
  ProbabilisticBundleSlotItemInterface: gql`
    fragment CardInRewardBox_ProbabilisticBundleSlotItemInterface on ProbabilisticBundleSlotItemInterface {
      id
      ... on ProbabilisticBundleSlotCardItem {
        id
        anyCard {
          slug
          displayRarity
          pictureUrl
          anyPlayer {
            slug
            displayName
          }
          ...CardProperties_anyCard
          ...TokenStatsIcon_anyCard
          ...getCardRepresentation_anyCardInterface
        }
      }
    }
    ${CardProperties.fragments.anyCard}
    ${TokenStatsIcon.fragments.anyCard}
    ${getCardRepresentation.fragments.anyCardInterface}
  ` as TypedDocumentNode<CardInRewardBox_ProbabilisticBundleSlotItemInterface>,
};

const CardInRewardBoxPreloader = ({ item }: CardInRewardBoxPreloaderProps) => {
  if (!CardInRewardBoxCanHandle(item)) {
    return null;
  }
  const { anyCard } = item;
  return <CardPreloader card={getCardRepresentation(anyCard)} />;
};

CardInRewardBox.preloader = CardInRewardBoxPreloader;

CardInRewardBox.canHandle = CardInRewardBoxCanHandle;

CardInRewardBox.description = (item: Props['item']) => {
  if (!CardInRewardBoxCanHandle(item)) {
    return null;
  }
  const {
    anyCard: {
      displayRarity,
      anyPlayer: { displayName },
    },
  } = item;
  return `${displayName} – ${displayRarity}`;
};

CardInRewardBox.fallback = (item: Props['item']) => {
  if (!CardInRewardBoxCanHandle(item)) {
    return null;
  }
  const { anyCard } = item;
  return <img src={anyCard.pictureUrl ?? ''} alt="" width="100" />;
};
